import { useEffect, useMemo, useRef, useState } from 'react'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js'
import { sceneSelection } from '../lib/sceneSelection'
import { liveTuneStore } from '../lib/liveTuneStore'
import { getAdapter, axesFor, modeAllowed } from '../lib/transformAdapters'
import * as history from '../lib/transformHistory'
import { gizmoWorldSize, uniformScaleFromDrag } from '../lib/gizmoScaleFeel'
import { rayPlaneHit, arcAngle, accumulate, snapAngle } from '../lib/rotateArcAngle.js'
import RotateGizmoRings, { handleFor, threeGizmoGroups } from './RotateGizmoRings.jsx'

/**
 * TransformGizmo — the viewport translate/rotate/scale handles (ENG-895).
 *
 * Uses three's own TransformControls (examples/jsm), NOT drei's wrapper. Two
 * reasons, both load-bearing:
 *   1. drei imports TransformControls from `three-stdlib`, a transitive dep
 *      versioned independently of our three. r184 changed the API (it now
 *      extends Controls, and you add `getHelper()` to the scene rather than the
 *      controls object) — exactly the kind of drift that breaks silently.
 *   2. drei's OrbitControls auto-disable keys off useThree(s => s.controls),
 *      which is only populated by `makeDefault`. Our <OrbitControls> doesn't
 *      set it, so that path would never fire in either mode.
 *
 * OWNERSHIP RULE — the core of this component. The gizmo is attached to a
 * PROXY Object3D, never to the splat or the avatar. The splat's transform is
 * owned by React props read from liveTuneStore (Scene.jsx), so a gizmo writing
 * the scene graph directly would be stomped on the next reconcile. Instead:
 *
 *   drag → proxy → 'objectChange' → adapter.write() → store → React → splat
 *
 * One owner per value. The proxy is re-seeded from adapter.read() whenever the
 * selection changes or the value moves underneath us (someone typing in the
 * numeric field), but NEVER mid-drag — that would fight the pointer.
 *
 * Camera stand-down: on 'dragging-changed' we set __ORBIT_CONTROLS__.enabled.
 * One mechanism covers both modes — in edit mode that's the real drei
 * OrbitControls, in live mode it's cameraRigCompat, whose stand-down path also
 * discharges momentum so a drag can't inherit a stale glide.
 */
export default function TransformGizmo() {
  const { camera, gl, scene } = useThree()
  const [, force] = useState(0)
  useEffect(() => sceneSelection.subscribe(() => force(n => n + 1)), [])

  const { selectedId, mode, space, snap } = sceneSelection.getState()
  const adapter = getAdapter(selectedId)
  const available = adapter?.isAvailable() ?? false
  const allowed = adapter ? modeAllowed(adapter.caps, mode) : false
  const active = Boolean(adapter) && available && allowed

  // The proxy the gizmo actually manipulates. Stable for the component's life;
  // it holds no state of its own beyond the current transform.
  const proxy = useMemo(() => new THREE.Object3D(), [])
  const controlsRef = useRef(null)
  const draggingRef = useRef(false)
  // The drag handler is installed once (mount effect) but needs the CURRENT
  // adapter, so it reads through a ref rather than closing over a stale one.
  const adapterRef = useRef(null)
  adapterRef.current = active ? adapter : null
  // Transform as it was when the current drag began; null when not dragging.
  const dragStartRef = useRef(null)
  // Shift held → finer scale control. Tracked from window events rather than
  // read off the drag event, because objectChange carries no modifier state and
  // the user can press/release Shift mid-drag.
  const fineRef = useRef(false)
  // Live pointer in NDC, needed by the rotate path: three's objectChange carries
  // no pointer position, and its own drag plane is EYE-aligned, not the rotation
  // plane, so controls.pointEnd cannot answer "what angle is the cursor at".
  const ndcRef = useRef({ x: 0, y: 0 })
  // Rotate drag state: the plane, the start orientation, and the accumulated
  // angle. Null when not rotating.
  const rotRef = useRef(null)

  // Built on FIRST selection, not on mount. TransformControls' constructor
  // builds nine handle groups — translate/rotate/scale, each with picker and
  // helper variants — plus three pointer listeners on the canvas. Every viewer
  // paid that on boot, including live/production sessions that can never open
  // the outliner (?tune=1 gated). Once built it is kept: re-selecting is
  // common, rebuilding is not free, and the idle cost of a detached instance
  // is nil (three skips its hover raycast when nothing is attached).
  const everActiveRef = useRef(false)
  if (active) everActiveRef.current = true
  const built = everActiveRef.current

  // Create once per camera/canvas. TransformControls attaches its own pointer
  // listeners to the dom element, so it must be disposed on teardown.
  useEffect(() => {
    if (!built) return
    const controls = new TransformControls(camera, gl.domElement)
    controlsRef.current = controls
    const helper = controls.getHelper()
    scene.add(helper)
    scene.add(proxy)

    // One passive listener for the pointer, on the same element three listens to.
    const onPointerMove = (ev) => {
      const r = gl.domElement.getBoundingClientRect()
      if (!r.width || !r.height) return
      ndcRef.current = {
        x: ((ev.clientX - r.left) / r.width) * 2 - 1,
        y: -((ev.clientY - r.top) / r.height) * 2 + 1,
      }
    }
    gl.domElement.addEventListener('pointermove', onPointerMove, { passive: true })

    /** The rotation axis in WORLD space for the current mode/space. */
    const worldAxis = () => {
      const axis = controls.axis // 'X' | 'Y' | 'Z' | 'E' | 'XYZE'
      const unit = axis === 'X' ? [1, 0, 0] : axis === 'Y' ? [0, 1, 0] : axis === 'Z' ? [0, 0, 1] : null
      if (!unit) {
        // 'E' / 'XYZE' rotate about the view direction — the eye vector IS the
        // plane normal, which the same arc math handles unchanged.
        const eye = new THREE.Vector3().subVectors(camera.position, proxy.position).normalize()
        return [eye.x, eye.y, eye.z]
      }
      if (controls.space === 'local') {
        const v = new THREE.Vector3(...unit).applyQuaternion(proxy.quaternion).normalize()
        return [v.x, v.y, v.z]
      }
      return unit
    }

    /** Where the cursor currently sits on the rotation plane, or null edge-on. */
    const pointerOnPlane = (axis, centre) => {
      const origin = camera.position
      const target = new THREE.Vector3(ndcRef.current.x, ndcRef.current.y, 0.5).unproject(camera)
      const dir = target.sub(origin)
      return rayPlaneHit([origin.x, origin.y, origin.z], [dir.x, dir.y, dir.z], centre, axis)
    }

    const onDraggingChanged = (e) => {
      draggingRef.current = e.value
      // Stand the camera down for the duration of the drag, then hand it back.
      const oc = typeof window !== 'undefined' ? window.__ORBIT_CONTROLS__ : null
      if (oc) oc.enabled = !e.value

      // Snapshot the transform the gesture started from. Serves two purposes:
      //   1. A 'uniform' scale adapter needs a FIXED baseline to tell which
      //      axis is being dragged; comparing against the live (already
      //      updated) value flips the answer mid-drag. See collapseUniformScale.
      //   2. It is exactly the undo entry. A gizmo has a real gesture boundary,
      //      so one drag is one Ctrl+Z with no coalescing heuristic.
      const a = adapterRef.current
      if (e.value) {
        dragStartRef.current = a ? a.read() : null
        if (a && dragStartRef.current) history.begin(a.id, dragStartRef.current)

        // Rotate: capture the plane and the pointer's opening angle, so the
        // object can track the cursor 1:1 (see rotateArcAngle.js for why three's
        // own rate-based mapping is what felt unresponsive).
        rotRef.current = null
        if (controls.mode === 'rotate') {
          const centre = [proxy.position.x, proxy.position.y, proxy.position.z]
          const axis = worldAxis()
          const hit = pointerOnPlane(axis, centre)
          if (hit) {
            const opening = arcAngle(hit, centre, axis)
            rotRef.current = {
              axis, centre,
              // Which HANDLE is being turned, for the visuals — three's label
              // ('X'|'Y'|'Z'|'E'|'XYZE') mapped through the rings' own table so
              // the two can't drift apart.
              ring: handleFor(controls.axis),
              startQuat: proxy.quaternion.clone(),
              startAngle: opening,
              prev: opening,
              total: 0,
            }
          }
        }
      } else {
        rotRef.current = null
        // Prune a press-and-release that never moved, so a stray click on a
        // handle doesn't cost the user an undo step.
        if (a) history.commit(a.id, a.read())
        dragStartRef.current = null
      }

      // On release, pull the proxy back in line with what the adapter actually
      // stored. It can legitimately differ: a 'uniform' scale adapter collapses
      // a per-axis drag to one factor, and clamps. Without this the handles
      // keep showing the non-uniform pose the pointer drew, while the object
      // renders the collapsed one.
      if (!e.value && adapterRef.current) {
        const t = adapterRef.current.read()
        proxy.position.set(...t.position)
        proxy.rotation.set(...t.rotation)
        proxy.scale.set(...t.scale)
        proxy.updateMatrixWorld()
      }
    }
    controls.addEventListener('dragging-changed', onDraggingChanged)

    // objectChange is attached HERE, in the same effect that owns `controls`,
    // rather than in its own adapter-keyed effect. A separate effect would keep
    // its listener bound to whichever instance existed when it last ran, so any
    // recreation of the controls (camera/canvas identity change) would silently
    // leave the write path listening to a dead object — the gizmo still drags
    // the proxy, but nothing is ever stored. It reads the live adapter through
    // adapterRef for the same reason.
    const onObjectChange = () => {
      const a = adapterRef.current
      if (!a) return

      // Uniform scale: recompute the factor from radial drag distance instead of
      // taking three's ratio off the proxy. three divides by how far from the
      // origin you grabbed, so the centre box — which sits AT the origin, and is
      // the obvious handle for uniform scale — is hypersensitive and impossible
      // to fine-tune. See gizmoScaleFeel.
      let scale = [proxy.scale.x, proxy.scale.y, proxy.scale.z]
      const startScale = dragStartRef.current?.scale?.[0]
      if (controls.dragging && controls.mode === 'scale'
          && a.caps?.scale === 'uniform' && Number.isFinite(startScale)) {
        const g = gizmoWorldSize({
          cameraDistance: camera.position.distanceTo(proxy.position),
          fovDeg: camera.fov,
          zoom: camera.zoom,
          size: controls.size,
          orthoHeight: camera.isOrthographicCamera ? (camera.top - camera.bottom) : null,
        })
        const s = uniformScaleFromDrag({
          startScale,
          r0: controls.pointStart.length(),
          r1: controls.pointEnd.length(),
          gizmoSize: g,
          fine: fineRef.current,
        })
        scale = [s, s, s]
        // Keep the handles showing what was actually applied, so the gizmo and
        // the object never disagree mid-drag.
        proxy.scale.set(s, s, s)
      }

      // Rotate: replace three's rate-based angle with direct pointer tracking.
      // three has already written its own rotation onto the proxy by the time
      // this fires, so we overwrite it — the same interception the uniform-scale
      // branch above performs, and for the same reason: its mapping is wrong for
      // the feel, not for the plumbing.
      const rot = rotRef.current
      if (controls.dragging && controls.mode === 'rotate' && rot) {
        const hit = pointerOnPlane(rot.axis, rot.centre)
        if (hit) {
          const next = accumulate(rot.total, rot.prev, arcAngle(hit, rot.centre, rot.axis))
          rot.total = next.total
          rot.prev = next.prev
        }
        // Shift = 5° snapping, matching PlayCanvas' snapIncrement behaviour.
        // sceneSelection is read LIVE here, not closed over: this effect runs
        // once, so a captured `snap` would be whatever it was at mount — the
        // same staleness trap adapterRef exists to avoid. Snap is in degrees
        // (see sceneSelection's DEFAULTS).
        const liveSnap = sceneSelection.getState().snap
        const applied = fineRef.current
          ? snapAngle(rot.total, 5 * Math.PI / 180)
          : snapAngle(rot.total, liveSnap?.rotate ? liveSnap.rotate * Math.PI / 180 : 0)
        // Quaternion, not Euler: composing on an axis avoids gimbal surprises,
        // and it works unchanged for the view-aligned 'E'/'XYZE' rings.
        const q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(...rot.axis).normalize(), applied)
        proxy.quaternion.copy(rot.startQuat).premultiply(q)
        proxy.updateMatrixWorld()
      }

      a.write({
        position: [proxy.position.x, proxy.position.y, proxy.position.z],
        rotation: [proxy.rotation.x, proxy.rotation.y, proxy.rotation.z],
        scale,
      }, dragStartRef.current)
    }
    controls.addEventListener('objectChange', onObjectChange)

    // Hide three's rotate ARCS and keep its invisible PICKERS: RotateGizmoRings
    // draws PlayCanvas-style rings at the same 0.5 radius those pickers use, so
    // what you see is still exactly what you grab. Guarded on the structure
    // rather than assumed — a three upgrade that renames these groups must fail
    // visibly (two gizmos) rather than silently drawing nothing.
    // DETACH, don't hide. TransformControlsGizmo.updateMatrixWorld reassigns
    // `gizmo.rotate.visible = (mode === 'rotate')` and then `handle.visible =
    // true` for every child, EVERY frame — so any `visible = false` we set is
    // overwritten before the next draw, which is exactly why three's arc kept
    // showing through. Removing the children is stable: that same function
    // builds its handle list from `.children`, so an empty group is simply
    // skipped, and `picker.rotate` — which owns hit-testing — is untouched.
    //
    // The geometries are deliberately NOT disposed: three's materials come from
    // a shared lib used by translate and scale too, and the arc geometries are
    // five tiny buffers on one long-lived gizmo instance. Freeing them is not
    // worth the risk of disposing something another mode still draws with.
    const threeGizmo = threeGizmoGroups(controls)
    if (threeGizmo?.gizmo?.rotate) threeGizmo.gizmo.rotate.clear()
    else console.warn('[TransformGizmo] three\'s rotate gizmo group not found — its arcs will draw alongside ours')

    if (typeof window !== 'undefined') {
      window.__TRANSFORM_GIZMO__ = {
        get selectedId() { return adapterRef.current?.id ?? null },
        get dragging() { return draggingRef.current },
        // Live rotate angle in DEGREES for the drag in progress, or null. The
        // readout PlayCanvas draws beside the ring reads this.
        get rotateDegrees() {
          const r = rotRef.current
          return r ? r.total * 180 / Math.PI : null
        },
        get rotateRing() { return rotRef.current?.ring ?? null },
        // three's own gizmo object: { gizmo, picker, helper } × { translate,
        // rotate, scale }. Exposed because the rotate DRAWING is ours while the
        // PICKING is still three's, and that split is only checkable from both
        // halves at once — a picker with no ring drawn over it is an invisible
        // handle.
        get threeGizmo() { return threeGizmoGroups(controls) },
        controls,
        proxy,
      }
    }

    return () => {
      gl.domElement.removeEventListener('pointermove', onPointerMove)
      controls.removeEventListener('dragging-changed', onDraggingChanged)
      controls.removeEventListener('objectChange', onObjectChange)
      if (typeof window !== 'undefined' && window.__TRANSFORM_GIZMO__?.controls === controls) {
        delete window.__TRANSFORM_GIZMO__
      }
      // Never leave the camera stood down because the gizmo unmounted mid-drag.
      const oc = typeof window !== 'undefined' ? window.__ORBIT_CONTROLS__ : null
      if (oc) oc.enabled = true
      controls.detach()
      scene.remove(helper)
      scene.remove(proxy)
      controls.dispose()
      controlsRef.current = null
    }
  }, [built, camera, gl, scene, proxy])

  // Attach / detach + per-mode configuration.
  useEffect(() => {
    const controls = controlsRef.current
    if (!controls) return
    if (!active) {
      controls.detach()
      return
    }
    controls.attach(proxy)
    controls.setMode(mode)
    controls.setSpace(space)
    controls.setTranslationSnap(snap.translate ?? null)
    controls.setRotationSnap(snap.rotate != null ? THREE.MathUtils.degToRad(snap.rotate) : null)
    controls.setScaleSnap(snap.scale ?? null)

    const axes = axesFor(adapter.caps, mode)
    controls.showX = axes.x
    controls.showY = axes.y
    controls.showZ = axes.z
  }, [active, adapter, mode, space, snap, proxy])

  // Track Shift for fine scale control. Window-level (not canvas) so releasing
  // Shift registers even if the pointer left the canvas mid-drag.
  useEffect(() => {
    if (!built) return
    const sync = (e) => { fineRef.current = e.shiftKey }
    window.addEventListener('keydown', sync)
    window.addEventListener('keyup', sync)
    return () => {
      window.removeEventListener('keydown', sync)
      window.removeEventListener('keyup', sync)
    }
  }, [built])

  // Seed the proxy from the adapter, and keep it in sync when the value moves
  // underneath us (panel edit, blueprint write, environment swap). Skipped
  // while dragging so the pointer always wins.
  useEffect(() => {
    if (!active) return
    const sync = () => {
      if (draggingRef.current) return
      const t = adapter.read()
      proxy.position.set(...t.position)
      proxy.rotation.set(...t.rotation)
      proxy.scale.set(...t.scale)
      proxy.updateMatrixWorld()
    }
    sync()
    // liveTuneStore is the only external writer for slice-1 adapters; the
    // avatar group is mutated in place (no notify), which is fine — its own
    // drags re-seed on the next selection/mode change.
    return liveTuneStore.subscribe(sync)
  }, [active, adapter, mode, proxy])

  // Rotate visuals. Rendered only in rotate mode and only once something is
  // selected — the rings ARE the rotate gizmo now, so with nothing attached
  // there is nothing to draw. The drag state is handed over as the REF, not its
  // value: the rings read it inside useFrame, so a drag starting or ending needs
  // no React re-render at all (CLAUDE.md — nothing on the hot path).
  const ringAxes = active && mode === 'rotate' ? axesFor(adapter.caps, 'rotate') : null

  return ringAxes ? (
    <RotateGizmoRings
      target={proxy}
      axes={ringAxes}
      // The REF, not `.current` — `controlsRef` is populated in the build effect,
      // so passing its value made the rings depend on a re-render happening
      // afterwards. When one didn't, they got null and every read fell back
      // silently: no hover colour, no local-space orientation, and no drag
      // appearance at all, with nothing logged. Reading the ref in useFrame has
      // no such ordering.
      controlsRef={controlsRef}
      dragRef={rotRef}
    />
  ) : null
}
